import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { Invoice } from "../../interfaces/screens/billing";
import { TDocumentDefinitions } from "pdfmake/interfaces";


// Attach the vfs fonts to pdfMake
// @ts-ignore
(pdfMake as any).vfs = pdfFonts.pdfMake?.vfs;

// Register Arabic-supporting font
(pdfMake as any).fonts = {
  Roboto: {
    normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
    bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
    italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
    bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf',
  },
};

const svgToPngDataURL = (svgString: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      // You might need to adjust dimensions for high-res displays
      const scale = window.devicePixelRatio || 1;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        const pngDataUrl = canvas.toDataURL('image/png');
        URL.revokeObjectURL(url);
        resolve(pngDataUrl);
      } else {
        URL.revokeObjectURL(url);
        reject(new Error('Could not get canvas context'));
      }
    };

    img.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };

    img.src = url;
  });
};

const generateInvoicePdf = async (invoice: Invoice, logoSvg: string) => {
  try {
    const logoDataUrl = await svgToPngDataURL(logoSvg);
    const calculatedTotalAmount = invoice.line_items.reduce((total, item) => total + item.quantity * item.unit_price, 0);

    const docDefinition: TDocumentDefinitions = {
      content: [
        // Header with Logo and Invoice Info
        {
          columns: [
            {
              image: logoDataUrl,
              width: 150,
            },
            {
              stack: [
                { text: `Invoice #${invoice.invoice_number}`, style: 'header' },
                { text: `Issue Date: ${invoice.issue_date}`, style: 'invoiceInfo' },
                { text: `Due Date: ${invoice.due_date}`, style: 'invoiceInfo' },
                { text: `Status: ${invoice.status}`, style: 'invoiceInfo' },
              ],
              alignment: 'right',
            },
          ],
          margin: [0, 0, 0, 20],
        },

        // Bill To Section (Placeholder)
        { text: 'Bill To:', style: 'subheader' },
        { text: `Patient ID: ${invoice.patient_id}`, margin: [0, 5, 0, 20] },


        // Line Items Table
        { text: 'Line Items', style: 'subheader' },
        {
          style: 'itemsTable',
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              // Table Header
              [
                { text: 'Description', style: 'tableHeader' },
                { text: 'Quantity', style: 'tableHeader', alignment: 'right' },
                { text: 'Unit Price', style: 'tableHeader', alignment: 'right' },
                { text: 'Total', style: 'tableHeader', alignment: 'right' },
              ],
              // Table Body
              ...invoice.line_items.map(item => [
                item.description,
                { text: item.quantity, alignment: 'right' },
                { text: `$${item.unit_price.toFixed(2)}`, alignment: 'right' },
                { text: `$${(item.quantity * item.unit_price).toFixed(2)}`, alignment: 'right' }
              ])
            ]
          },
          layout: {
            fillColor: function (rowIndex) {
              // Keep header white, and zebra stripe the rest
              return (rowIndex > 0 && rowIndex % 2 !== 0) ? '#F5F5F5' : null;
            },
            // A solid line under the header, a matching one at the bottom, and lighter lines for rows. No vertical lines.
            hLineWidth: function (i, node) {
              if (i === 1 || i === node.table.body.length) {
                return 1; // Line under header and at the table bottom
              }
              return (i > 0 && i < node.table.body.length) ? 0.5 : 0; // Thinner lines for other rows, no top border
            },
            vLineWidth: function () {
              return 0; // No vertical lines
            },
            hLineColor: function (i) {
              return i > 0 ? '#DDDDDD' : 'black';
            },
            paddingTop: function() { return 8; },
            paddingBottom: function() { return 8; },
            paddingLeft: function() { return 8; },
            paddingRight: function() { return 8; }
          }
        },

        // Totals section
        {
          columns: [
            { text: '', width: '*' },
            {
              width: 'auto',
              table: {
                body: [
                  ['Total Amount', `$${calculatedTotalAmount.toFixed(2)}`],
                  ['Balance Due', `$${invoice.balance_due.toFixed(2)}`],
                ],
              },
              layout: 'noBorders',
            },
          ],
          margin: [0, 20, 0, 0]
        },

        // Footer
        {
          text: 'Thank you for your business!',
          style: 'footer',
          alignment: 'center',
          margin: [0, 40, 0, 0],
        }
      ],
      styles: {
        header: {
          fontSize: 22,
          bold: true,
        },
        invoiceInfo: {
          fontSize: 10,
          color: 'grey'
        },
        subheader: {
          fontSize: 14,
          bold: true,
          margin: [0, 15, 0, 5],
        },
        itemsTable: {
            margin: [0, 5, 0, 15]
        },
        tableHeader: {
            bold: true,
            fontSize: 12,
            color: 'black'
        },
        footer: {
            fontSize: 10,
            italics: true
        }
      },
      defaultStyle: {
        font: 'Roboto',
        fontSize: 10,
      },
    };
  
    pdfMake.createPdf(docDefinition).download(`Invoice-${invoice.invoice_number}.pdf`);
  } catch (error) {
    console.error("Error during PDF generation:", error);
    alert("Failed to generate PDF. The logo image could not be processed.");
  }
};

export { generateInvoicePdf }; 